import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { withStyles } from 'tss-react/mui';
import { Box, Typography } from '@mui/material';
import { confirmRematching } from '@uplift/api';
import { CalendarDateService } from '@uplift/services';
import { Alert, formatCents, Spinner } from '@uplift/ui-v2';
import { useCurrentuser } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { KeepOldCard } from '../components/ProviderInfoCard/keep-old-card.component';
import { ProviderConfirmationCard } from '../components/ProviderInfoCard/confirmation-card.component';
import { RematchingContainer } from '../components/RematchingContainer';
import { lateFeeAmountByAppointmentType } from '@uplift/utils';
import { usePatientNextAppointmentsQuery, useProviderPublicMatchingProfile } from '@uplift/queries';
import { AddressType } from '@uplift/types';
import { MatchingData } from './4-providers.page';
import { RematchingStateMachineContext } from '../state-machine/machine';


export type RematchingConfirmPageProps = {
  onConfirm: () => void;
  onReject: () => void;
  oldMatchId: string;
  oldProviderId: string;
  selectedSlot: RematchingStateMachineContext['selectedSlot'];
  newProvider: RematchingStateMachineContext['newProvider'];
  rematchReasons: string[];
  details?: string;
  newAddress: AddressType | null;

  matchingData: MatchingData;
};

const RematchingConfirmContainer = withStyles(RematchingContainer, theme => ({
  container: {
    paddingBottom: theme.spacing(5),
    [theme.breakpoints.up('sm')]: {
      paddingBottom: theme.spacing(10),
    }
  },
  content: {
    maxWidth: '486px',
  }
}));


export const RematchingConfirmPage: React.VFC<RematchingConfirmPageProps> = props => {
  const queryClient = useQueryClient();
  const currentUser = useCurrentuser();
  const l10n = useL10n();
  const {
    mutate: mutateConfirmRematching,
    isLoading: isConfirmRematchingLoading,
    error: confirmRematchingError
  } = useMutation(confirmRematching, {
    onSuccess(response) {
      if (response.success) {
        queryClient.invalidateQueries([ 'user', currentUser.id, 'matches' ]);
        props.onConfirm();
      }
      if (response.error) {
        const error = l10n.schedule.createAppointment.errors[response.error]?.body || response.error;

        throw error;
      }
    }
  });

  const { data: nextAppointments } = usePatientNextAppointmentsQuery({
    provider_id: props.oldProviderId,
    dt_from: CalendarDateService.date.utc().subtract(2, 'hours').format(),
    limit: 1,
  });
  const hasLateFee = React.useMemo(() => {
    const nextAppointment = nextAppointments?.[0];

    if (nextAppointment) {
      const fromDatetime = CalendarDateService.date(nextAppointment.fromDatetime);

      return fromDatetime.isBefore(CalendarDateService.date.utc().add(24, 'hours'));
    }

    return false;
  }, [ nextAppointments ]);

  const { data: oldProvider, isInitialLoading: isLoading } = useProviderPublicMatchingProfile(props.oldProviderId, { enabled: !!props.oldProviderId });

  const translatedRematchingReasons = React.useMemo(() => {
    return props.rematchReasons.map(r => l10n.rematching.rematchReasons[r] || r);
  }, [ l10n, props.rematchReasons ]);

  if (isLoading) {
    return (
      <Box my={20}>
        <Spinner />
      </Box>
    );
  }

  const handleConfirm = async () => {
    mutateConfirmRematching({
      timeStart: CalendarDateService.date(props.selectedSlot.fromDatetime).format(),
      providerId: props.newProvider.id,
      oldMatchId: props.oldMatchId === 'new' ? null : props.oldMatchId,
      details: props.details,
      rematchingReasons: translatedRematchingReasons,
      matchPreferences: {
        stateId: props.matchingData.stateId,
        insuranceCompanyId: props.matchingData.insuranceId,
        visitReasonIds: props.matchingData.specialtyIds,
        ageGroupId: props.matchingData.ageId,
        ethnicityIds: props.matchingData.identityFilters.ethnicityIds,
        faithIds: props.matchingData.identityFilters.faithIds,
        genderIds: props.matchingData.identityFilters.genderIds,
        communityExpertise: {
          faithIds: props.matchingData.experienceFilters.faithIds,
          ethnicityIds: props.matchingData.experienceFilters.ethnicityIds,
          generalIds: props.matchingData.experienceFilters.communityIds,
        },
      },
      newAddress: props.newAddress ? {
        line_1: props.newAddress.address1,
        line_2: props.newAddress.address2,
        city: props.newAddress.city,
        stateId: props.newAddress.state,
        zip: props.newAddress.zip,
      } : undefined,
    });
  };

  return (
    <RematchingConfirmContainer>
      <Typography variant="h3" align="center">
        Confirm your <Typography variant="h3" component="span" color="secondary">new therapist</Typography>
      </Typography>

      {oldProvider && (
        <Box mt={3}>
          <Alert severity="info">
            When you confirm your new therapist, any future appointments with {oldProvider.nameDisplay.full} will be cancelled{hasLateFee && (
              <span>
                . Your next session with {oldProvider.nameDisplay.full} is scheduled within 24 hours, so you will be charged a {formatCents(lateFeeAmountByAppointmentType.therapy)} late cancel fee.
              </span>
            )}
          </Alert>
        </Box>
      )}

      <Box mt={4}>
        <ProviderConfirmationCard
          error={confirmRematchingError as string}
          isLoading={isConfirmRematchingLoading}
          appointment={props.selectedSlot}
          provider={props.newProvider}
          onConfirm={handleConfirm}
        />

        {oldProvider && (
          <>
            <Box mt={7} mb={3}>
              <Typography variant="h4" align="center">
                Decided you don&apos;t want to make a change?
              </Typography>
            </Box>

            <KeepOldCard
              disabled={isConfirmRematchingLoading}
              reasons={translatedRematchingReasons}
              provider={{
                id: props.oldProviderId,
                first_name: oldProvider.nameDisplay.first,
                last_name: oldProvider.nameDisplay.last,
                avatar_url: oldProvider.avatarAsset?.url,
              }}
              onReject={props.onReject}
            />
          </>
        )}

      </Box>
    </RematchingConfirmContainer>
  );
};
